export type XummProxyOk<T> = {
  ok: true;
  data: T;
};

export type XummProxyErr = {
  ok: false;
  error: string;
  status?: number;
  data?: unknown;
};

export type XummProxyResponse<T> = XummProxyOk<T> | XummProxyErr;

export type XummCreatePayloadResponse = {
  uuid: string;
  next?: {
    always?: string;
    no_push_msg_received?: string;
  };
  refs?: {
    qr_png?: string;
    qr_matrix?: string;
    qr_uri_quality_opts?: string[];
  };
  pushed?: boolean;
};

export type XummPayloadStatusResponse = {
  meta?: {
    exists?: boolean;
    uuid?: string;
    signed?: boolean;
    resolved?: boolean;
    cancelled?: boolean;
    expired?: boolean;
    app_opened?: boolean;
    opened_by_deeplink?: boolean;
  };
  application?: unknown;
  payload?: unknown;
  response?: {
    account?: string;
    txid?: string;
    // other fields exist but we don’t rely on them
    [k: string]: unknown;
  };
  custom_meta?: unknown;
};

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(url, init);
  const text = await resp.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    data = { raw: text };
  }

  if (!resp.ok) {
    const maybeObj = (typeof data === 'object' && data !== null)
      ? (data as Record<string, unknown>)
      : null;
    const msg = (maybeObj && typeof maybeObj.error === 'string')
      ? maybeObj.error
      : `HTTP ${resp.status}`;
    throw new Error(msg);
  }

  return data as T;
}

export async function createXummPayload(txjson: Record<string, unknown>, extras?: {
  options?: Record<string, unknown>;
  custom_meta?: Record<string, unknown>;
}): Promise<XummCreatePayloadResponse> {
  const body: Record<string, unknown> = { txjson };
  if (extras?.options) body.options = extras.options;
  if (extras?.custom_meta) body.custom_meta = extras.custom_meta;

  const proxy = await fetchJson<XummProxyResponse<XummCreatePayloadResponse>>('/api/xumm/payload', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!proxy.ok) {
    throw new Error(proxy.error || 'XUMM proxy error');
  }

  return proxy.data;
}

export async function getXummPayload(uuid: string): Promise<XummPayloadStatusResponse> {
  const proxy = await fetchJson<XummProxyResponse<XummPayloadStatusResponse>>(`/api/xumm/payload/${encodeURIComponent(uuid)}`);
  if (!proxy.ok) {
    throw new Error(proxy.error || 'XUMM proxy error');
  }
  return proxy.data;
}

/**
 * Create payload and subscribe to events (no polling!)
 * This is the CORRECT way per Xaman requirements - uses SDK events instead of polling
 * 
 * @param txjson - Transaction JSON to sign
 * @param onEvent - Callback for payload events (signed, rejected, etc.)
 * @param extras - Optional metadata and options
 * @returns Created payload with UUID and refs
 */
export async function createAndSubscribePayload(
  txjson: Record<string, unknown>,
  onEvent: (event: { meta?: { uuid?: string }; data?: { signed?: boolean; rejected?: boolean; txid?: string }; [k: string]: unknown }) => void,
  extras?: {
    options?: Record<string, unknown>;
    custom_meta?: Record<string, unknown>;
  }
): Promise<XummCreatePayloadResponse> {
  // Import xApp SDK dynamically to avoid issues in non-xApp contexts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let xApp: any
  try {
    const module = await import('xumm-xapp-sdk')
    xApp = module.xApp
  } catch (error) {
    console.warn('[XUMM] xApp SDK not available, using traditional flow:', error)
  }
  
  // First, create the payload via our backend proxy
  const payload = await createXummPayload(txjson, extras)
  
  // If we're in xApp context and SDK is available, set up event listener
  if (xApp) {
    try {
      const sdk = new xApp()
      const env = sdk.getEnvironment()
      
      if (env.ott) {
        // Running in Xaman - use SDK events (compliant, no polling)
        console.log('[XUMM] In xApp context, subscribing to payload events for:', payload.uuid)
        
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handler = (event: any) => {
          // Filter events for this specific payload
          if (event.meta?.uuid === payload.uuid) {
            console.log('[XUMM] Payload event received:', event)
            onEvent(event)
          }
        }
        
        sdk.on('payload', handler)
        
        // Open the sign request in Xaman
        await sdk.payload.openSignRequest({ uuid: payload.uuid })
      } else {
        console.log('[XUMM] In browser context - will show QR code')
      }
    } catch (error) {
      console.warn('[XUMM] Error setting up SDK event listener:', error)
    }
  }
  
  return payload
}
